import React, { useCallback, useRef, useState } from "react";

import { GoalCaptureCell } from "@components/ChartCapture/GoalCaptureCell";
import { TargetCaptureCell } from "@components/ChartCapture/TargetCaptureCell";
import { CaptureJob, CaptureResult, CellOutcome } from "@components/ChartCapture/types";
import { captureJobId } from "@components/ChartCapture/utils/captureJobId";

const DEFAULT_CONCURRENCY = 4;

// ApexCharts needs a laid-out box to render and export — `display:none` yields a
// zero-size canvas and a blank dataURI. So the charts render at real dimensions,
// pushed off-screen instead of hidden.
const CONTAINER_STYLE: React.CSSProperties = {
  position: "fixed",
  left: -99999,
  top: 0,
  width: 900,
  opacity: 0,
  pointerEvents: "none",
  zIndex: -1,
};
const CELL_STYLE: React.CSSProperties = { width: 900, height: 480 };

type OffscreenChartCaptureRunnerProps = {
  jobs: CaptureJob[];
  /** Max charts mounted (and thus capturing) at once. */
  concurrency?: number;
  /** Aggregate progress (used by the sign-time backfill). */
  onProgress?: (done: number, total: number) => void;
  /** Fires when a job's chart starts rendering (its cell mounts). */
  onItemStart?: (job: CaptureJob) => void;
  /** Fires per job the moment its capture settles — before any downstream save. */
  onItemCaptured?: (job: CaptureJob, result: CaptureResult) => void;
  /** Fires once, after every job has been captured. */
  onComplete?: (results: CaptureResult[]) => void;
};

/**
 * Renders charts off-screen as a sliding window of `concurrency` slots: as soon
 * as any chart finishes capturing, the next queued job takes its place, so the
 * pool stays full instead of waiting for a whole batch (no per-batch barrier).
 * Reports results per item (`onItemStart` / `onItemCaptured`, for a live
 * pipeline) and in aggregate (`onProgress` / `onComplete`). Mount it only while a
 * run is active (`jobs` non-empty) so a fresh mount resets state.
 */
export const OffscreenChartCaptureRunner: React.FC<OffscreenChartCaptureRunnerProps> = ({
  jobs,
  concurrency = DEFAULT_CONCURRENCY,
  onProgress,
  onItemStart,
  onItemCaptured,
  onComplete,
}) => {
  // Indices of the jobs currently mounted (the active window).
  const [active, setActive] = useState<number[]>(() => jobs.map((_, i) => i).slice(0, concurrency));
  // How many jobs have been dispatched into the window so far.
  const startedRef = useRef(Math.min(concurrency, jobs.length));
  const completedRef = useRef(0);
  const resultsRef = useRef<CaptureResult[]>([]);
  const onItemStartRef = useRef(onItemStart);
  onItemStartRef.current = onItemStart;

  const handleCellResult = useCallback(
    (index: number, job: CaptureJob, outcome: CellOutcome) => {
      const result: CaptureResult = { job, ...outcome };
      resultsRef.current = [...resultsRef.current, result];
      completedRef.current += 1;
      onProgress?.(completedRef.current, jobs.length);
      onItemCaptured?.(job, result);

      // Free this slot and pull the next queued job into the window.
      const nextIndex = startedRef.current < jobs.length ? startedRef.current : -1;
      if (nextIndex >= 0) startedRef.current += 1;
      setActive((prev) => {
        const without = prev.filter((i) => i !== index);
        return nextIndex >= 0 ? [...without, nextIndex] : without;
      });

      if (completedRef.current >= jobs.length) onComplete?.(resultsRef.current);
    },
    [jobs, onProgress, onItemCaptured, onComplete],
  );

  return (
    <div aria-hidden style={CONTAINER_STYLE} data-testid="offscreen-chart-capture">
      {active.map((index) => {
        const job = jobs[index];
        const onStart = () => onItemStartRef.current?.(job);
        return (
          <div key={captureJobId(job)} style={CELL_STYLE}>
            {job.kind === "goal" ? (
              <GoalCaptureCell
                goal={job.goal}
                onStart={onStart}
                onResult={(outcome) => handleCellResult(index, job, outcome)}
              />
            ) : (
              <TargetCaptureCell
                goalId={job.goalId}
                target={job.target}
                onStart={onStart}
                onResult={(outcome) => handleCellResult(index, job, outcome)}
              />
            )}
          </div>
        );
      })}
    </div>
  );
};
